import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';

interface BreadcrumbItem {
  level: string;
  value: string;
  label: string;
}

interface TreeChild {
  id: string;
  name: string;
  count: number;
  has_children: boolean;
}

interface TargetingItem {
  id: number;
  name: string;
  full_path: string;
  google_audience_id: string | null;
  google_audience_type: string | null;
  google_audience_name: string | null;
}

interface TreeData {
  level: number;
  breadcrumb: BreadcrumbItem[];
  children: TreeChild[];
  items: TargetingItem[];
}

interface SelectedItem {
  id: number;
  name: string;
  full_path: string;
  google_audience_id: string | null;
  google_audience_type: string | null;
  type?: string;
}

interface AdvancedTargetingProps {
  selectedItems: SelectedItem[];
  onSelectionChange: (items: SelectedItem[]) => void;
  disabled?: boolean;
}

type CategoryType = 'demographics' | 'intentions' | 'preferences';

interface CategoryConfig {
  id: CategoryType;
  /** Catalogue keys — the id stays the matching value, the label is only shown. */
  labelKey: string;
  descriptionKey: string;
  placeholderKey: string;
  icon: React.ReactNode;
}

const CATEGORIES: CategoryConfig[] = [
  {
    id: 'demographics',
    labelKey: 'targeting.catDemographics',
    descriptionKey: 'targeting.catDemographicsDesc',
    placeholderKey: 'targeting.addDemographics',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    id: 'intentions',
    labelKey: 'targeting.catIntentions',
    descriptionKey: 'targeting.catIntentionsDesc',
    placeholderKey: 'targeting.addIntentions',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
      </svg>
    ),
  },
  {
    id: 'preferences',
    labelKey: 'targeting.catPreferences',
    descriptionKey: 'targeting.catPreferencesDesc',
    placeholderKey: 'targeting.addPreferences',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    ),
  },
];

// Single category block component
const CategoryBlock: React.FC<{
  category: CategoryConfig;
  selectedItems: SelectedItem[];
  onToggleItem: (item: TargetingItem, categoryType: CategoryType) => void;
  onRemoveItem: (id: number) => void;
  disabled?: boolean;
}> = ({ category, selectedItems, onToggleItem, onRemoveItem, disabled }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState<TreeData | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<TargetingItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Navigation state
  const [navPath, setNavPath] = useState<string[]>([]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Filter selected items for this category
  const categoryItems = selectedItems.filter(item =>
    item.full_path?.toLowerCase().startsWith(category.id.toLowerCase())
  );

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch tree data
  useEffect(() => {
    if (isOpen) {
      fetchTreeData();
    }
  }, [isOpen, navPath]);

  // Search with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (searchValue.length >= 2) {
      setSearchLoading(true);
      searchTimeoutRef.current = setTimeout(() => {
        searchTargeting(searchValue);
      }, 300);
    } else {
      setSearchResults([]);
    }
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchValue]);

  const fetchTreeData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('type', category.id);

      if (navPath.length > 0) params.append('super_category', navPath[0]);
      if (navPath.length > 1) params.append('category', navPath[1]);
      if (navPath.length > 2) params.append('subcategory', navPath[2]);
      if (navPath.length > 3) params.append('detail', navPath[3]);

      const response = await api.get(`/api/targeting/advanced/tree?${params.toString()}`);
      setTreeData(response.data);
    } catch (error) {
      console.error('Error fetching targeting tree:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchTargeting = async (query: string) => {
    try {
      const response = await api.get(`/api/targeting/advanced/search`, {
        params: { q: query }
      });
      // Filter results to only this category
      const filtered = (response.data.results || []).filter((item: TargetingItem) =>
        item.full_path?.toLowerCase().startsWith(category.id.toLowerCase())
      );
      setSearchResults(filtered);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const navigateTo = (childId: string) => {
    setNavPath([...navPath, childId]);
  };

  const navigateBack = (index: number) => {
    if (index < 0) {
      setNavPath([]);
    } else {
      setNavPath(navPath.slice(0, index + 1));
    }
  };

  const handleToggle = (item: TargetingItem) => {
    onToggleItem(item, category.id);
  };

  const displayItems = searchValue.length >= 2 ? searchResults : (treeData?.items || []);
  const displayChildren = searchValue.length >= 2 ? [] : (treeData?.children || []);

  // Get selected count for a child category
  const getSelectedCount = (childId: string): number => {
    const prefix = [category.id, ...navPath, childId].join(' > ').toLowerCase();
    return selectedItems.filter(item =>
      item.full_path?.toLowerCase().startsWith(prefix)
    ).length;
  };

  return (
    <div className={`at-category-block at-category-${category.id}`}>
      {/* Header */}
      <div className="at-category-header">
        <span className="at-category-icon">{category.icon}</span>
        <div className="at-category-info">
          <span className="at-category-label">{t(category.labelKey)}</span>
          <span className="at-category-desc">{t(category.descriptionKey)}</span>
        </div>
        {categoryItems.length > 0 && (
          <span className="at-category-count">{categoryItems.length}</span>
        )}
      </div>

      {/* Selector */}
      <div className="at-selector-wrap" ref={dropdownRef}>
        <button
          className={`at-selector-trigger ${isOpen ? 'open' : ''}`}
          onClick={() => {
            if (!disabled) {
              setIsOpen(!isOpen);
              if (!isOpen) {
                setNavPath([]);
                setSearchValue('');
              }
            }
          }}
          disabled={disabled}
        >
          <span>{t(category.placeholderKey)}</span>
          <svg className="at-selector-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {isOpen && (
          <div className="at-dropdown">
            {/* Breadcrumb */}
            {navPath.length > 0 && (
              <div className="at-breadcrumb">
                <button className="at-breadcrumb-item at-breadcrumb-root" onClick={() => navigateBack(-1)}>
                  {t(category.labelKey)}
                </button>
                {navPath.map((segment, idx) => (
                  <React.Fragment key={idx}>
                    <span className="at-breadcrumb-sep">/</span>
                    <button
                      className={`at-breadcrumb-item ${idx === navPath.length - 1 ? 'current' : ''}`}
                      onClick={() => navigateBack(idx)}
                    >
                      {segment}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Search */}
            <div className="at-search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder={t('targeting.search')}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              {searchValue && (
                <button className="at-search-clear" onClick={() => setSearchValue('')}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* List */}
            <div className="at-list">
              {loading || searchLoading ? (
                <div className="at-loading">{t('targeting.loading')}</div>
              ) : (
                <>
                  {displayChildren.map((child) => {
                    const count = getSelectedCount(child.id);
                    return (
                      <div
                        key={child.id}
                        className={`at-list-item at-list-folder ${count > 0 ? 'has-selected' : ''}`}
                        onClick={() => navigateTo(child.id)}
                      >
                        <span className="at-item-name">{child.name}</span>
                        {count > 0 && <span className="at-item-badge">{count}</span>}
                        <span className="at-item-total">{child.count}</span>
                        <svg className="at-item-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </div>
                    );
                  })}

                  {displayItems.map((item) => {
                    const isSelected = selectedItems.some(s => s.id === item.id);
                    return (
                      <div
                        key={item.id}
                        className={`at-list-item at-list-leaf ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleToggle(item)}
                      >
                        <span className={`at-checkbox ${isSelected ? 'checked' : ''}`}>
                          {isSelected && (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </span>
                        <span className="at-item-name">{item.name}</span>
                        {searchValue && item.full_path && (
                          <span className="at-item-path">{item.full_path.split(' > ').slice(1).join(' › ')}</span>
                        )}
                      </div>
                    );
                  })}

                  {displayChildren.length === 0 && displayItems.length === 0 && (
                    <div className="at-empty">{t('targeting.noItems')}</div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Selected Tags */}
      {categoryItems.length > 0 && (
        <div className="at-tags">
          {categoryItems.map((item) => (
            <span key={item.id} className="at-tag">
              <span className="at-tag-text" title={item.full_path}>{item.name}</span>
              <button
                className="at-tag-remove"
                onClick={() => onRemoveItem(item.id)}
                disabled={disabled}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Main component
const AdvancedTargeting: React.FC<AdvancedTargetingProps> = ({
  selectedItems,
  onSelectionChange,
  disabled = false
}) => {
  const handleToggleItem = (item: TargetingItem, categoryType: CategoryType) => {
    const isSelected = selectedItems.some(s => s.id === item.id);

    if (isSelected) {
      onSelectionChange(selectedItems.filter(s => s.id !== item.id));
    } else {
      onSelectionChange([...selectedItems, {
        id: item.id,
        name: item.name,
        full_path: item.full_path,
        google_audience_id: item.google_audience_id,
        google_audience_type: item.google_audience_type,
        type: categoryType
      }]);
    }
  };

  const handleRemoveItem = (id: number) => {
    onSelectionChange(selectedItems.filter(s => s.id !== id));
  };

  return (
    <div className="at-container">
      {CATEGORIES.map((category) => (
        <CategoryBlock
          key={category.id}
          category={category}
          selectedItems={selectedItems}
          onToggleItem={handleToggleItem}
          onRemoveItem={handleRemoveItem}
          disabled={disabled}
        />
      ))}
    </div>
  );
};

export default AdvancedTargeting;
